<template>
  <v-card class="tournament-card h-100" elevation="1">
    <!-- Header con estado -->
    <div class="card-header" :class="getStatusClass(tournament.status)">
      <span class="status-badge">{{ getStatusLabel(tournament.status) }}</span>
      <v-menu v-if="isRp" offset-y small>
        <template #activator="{ on, attrs }">
          <v-btn icon small v-bind="attrs" v-on="on" class="ml-auto">
            <v-icon small>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item @click="$emit('edit', tournament)">
            <v-list-item-icon>
              <v-icon small>mdi-pencil</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Editar</v-list-item-title>
          </v-list-item>
          <v-list-item @click="$emit('delete', tournament)">
            <v-list-item-icon>
              <v-icon small>mdi-trash-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Eliminar</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <!-- Contenido -->
    <v-card-text class="pa-4">
      <h3 class="text-subtitle-1 font-weight-bold mb-2">{{ tournament.name }}</h3>
      <p class="grey--text text-caption mb-3" v-if="tournament.description">
        {{ truncate(tournament.description, 100) }}
      </p>

      <div class="tournament-details">
        <!-- Fecha -->
        <div class="detail-item mb-2">
          <v-icon small class="mr-2">mdi-calendar</v-icon>
          <span class="text-caption">{{ formatDate(tournament.start_date) }}</span>
        </div>

        <!-- Cancha -->
        <div class="detail-item mb-2">
          <v-icon small class="mr-2">mdi-golf</v-icon>
          <span class="text-caption">{{ tournament.course }}</span>
        </div>

        <!-- Handicap range -->
        <div class="detail-item mb-3">
          <v-icon small class="mr-2">mdi-calculator</v-icon>
          <span class="text-caption">
            HCP {{ tournament.handicap_min }} - {{ tournament.handicap_max }}
          </span>
        </div>

        <!-- Inscritos -->
        <v-progress-linear
          :value="getParticipationPercentage(tournament)"
          :color="tournament.is_full ? 'error' : 'primary'"
          class="mb-2"
        ></v-progress-linear>
        <div class="d-flex justify-space-between text-caption grey--text">
          <span>{{ tournament.current_players }} / {{ tournament.max_players }} inscritos</span>
          <span v-if="tournament.is_full" class="error--text font-weight-bold">Lleno</span>
          <span v-else class="success--text font-weight-bold">{{ tournament.available_slots }} libres</span>
        </div>
      </div>
    </v-card-text>

    <!-- Acciones -->
    <v-divider></v-divider>
    <v-card-actions>
      <v-btn small outlined text @click="$emit('view', tournament)">
        Ver Detalle
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn
        small
        color="primary"
        @click="$emit('register', tournament)"
        :disabled="tournament.is_full || [3, 6].includes(tournament.status)"
      >
        Inscribirse
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
export default {
  name: 'TournamentCard',
  props: {
    tournament: {
      type: Object,
      required: true
    },
    isRp: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    getStatusLabel (status) {
      const labels = {
        1: 'Pendiente',
        2: 'Confirmado',
        3: 'Cancelado',
        4: 'En curso',
        5: 'Finalizado',
        6: 'Cancelado'
      }
      return labels[status] || 'Desconocido'
    },
    getStatusClass (status) {
      const classes = {
        1: 'status-pending',
        2: 'status-confirmed',
        3: 'status-cancelled',
        4: 'status-active',
        5: 'status-finished',
        6: 'status-cancelled'
      }
      return classes[status] || ''
    },
    formatDate (date) {
      return new Date(date).toLocaleDateString('es-ES', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    },
    getParticipationPercentage (tournament) {
      return (tournament.current_players / tournament.max_players) * 100
    },
    truncate (text, length) {
      return text && text.length > length ? text.substring(0, length) + '...' : text
    }
  }
}
</script>

<style scoped>
.tournament-card {
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
}

.tournament-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
  transform: translateY(-2px);
}

.card-header {
  padding: 12px 16px;
  display: flex;
  align-items: center;
  border-bottom: 3px solid #ddd;
}

.status-badge {
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 4px;
  color: white;
}

.card-header.status-pending {
  background-color: #fff9c4;
  border-bottom-color: #f57f17;
}

.card-header.status-pending .status-badge {
  background-color: #f57f17;
}

.card-header.status-confirmed {
  background-color: #e8f5e9;
  border-bottom-color: #388e3c;
}

.card-header.status-confirmed .status-badge {
  background-color: #388e3c;
}

.card-header.status-active {
  background-color: #e3f2fd;
  border-bottom-color: #1976d2;
}

.card-header.status-active .status-badge {
  background-color: #1976d2;
}

.card-header.status-finished {
  background-color: #f3e5f5;
  border-bottom-color: #7b1fa2;
}

.card-header.status-finished .status-badge {
  background-color: #7b1fa2;
}

.card-header.status-cancelled {
  background-color: #eeeeee;
  border-bottom-color: #616161;
}

.card-header.status-cancelled .status-badge {
  background-color: #616161;
}

.tournament-details {
  margin: 8px 0;
}

.detail-item {
  display: flex;
  align-items: center;
  color: #666;
}

::v-deep .v-card__text {
  flex-grow: 1;
}

::v-deep .v-card__actions {
  padding: 8px 16px;
}
</style>
